/**
 * F2: the one way a contact's profile picture is drawn, anywhere in the app.
 *
 * `Customer.avatar` is a real `assets.MediaAsset` FK, so every customer payload
 * already carries a render-ready `avatar_image` (src + responsive srcset, see
 * `apps/insurers/serializers.py::MediaAssetBriefSerializer`). None of the
 * contact screens read it, and the circle they drew instead was either empty or
 * — where a bare `<img>` had been pointed at a missing url — a torn-image icon.
 *
 * Two rules follow from that report and both are enforced here rather than by
 * each caller:
 *
 *  1. There is never an `<img>` without a url behind it. No url, a url that
 *     404s at runtime, an `avatar_image` the API sent as `null` — all three
 *     collapse to the same placeholder, so a broken image is unreachable.
 *  2. The placeholder is deterministic. The colour is hashed from the contact
 *     *id*, not the name, so the same person is the same colour on the contacts
 *     list, the detail header, their own "My Contacts" screen and after a
 *     rename — a colour that reshuffles per render reads as a rendering bug.
 */

import { useId, useState } from 'react';
import { UserIcon } from '@heroicons/react/24/solid';

import { getErrorMessage } from '@/services/api';
import { cn } from '@/utils';

/**
 * The compact read shape the API sends beside the `avatar` FK. Deliberately a
 * local declaration and not the full `MediaAsset` from `@/services/assets`:
 * the brief carries only what is needed to draw the picture (no usage counts,
 * no permissions), and `types/index.ts` is outside this component's ownership.
 * Every field but `id` is optional so a partial payload can never throw.
 */
export interface ContactAvatarImage {
  id?: string;
  src?: string | null;
  srcset?: string | null;
  srcset_webp?: string | null;
  url?: string | null;
  stable_url?: string | null;
  alt_text?: string | null;
  placeholder_color?: string | null;
  width?: number | null;
  height?: number | null;
}

/**
 * Eight light/dark pairs. Kept as whole literal class strings because Tailwind
 * scans source text — a template-built `bg-${colour}-100` would be purged out
 * of the production stylesheet and every placeholder would render transparent.
 */
const AVATAR_PALETTE = [
  'bg-rose-100 text-rose-700 dark:bg-rose-500/25 dark:text-rose-100',
  'bg-amber-100 text-amber-700 dark:bg-amber-500/25 dark:text-amber-100',
  'bg-emerald-100 text-emerald-700 dark:bg-emerald-500/25 dark:text-emerald-100',
  'bg-teal-100 text-teal-700 dark:bg-teal-500/25 dark:text-teal-100',
  'bg-sky-100 text-sky-700 dark:bg-sky-500/25 dark:text-sky-100',
  'bg-indigo-100 text-indigo-700 dark:bg-indigo-500/25 dark:text-indigo-100',
  'bg-violet-100 text-violet-700 dark:bg-violet-500/25 dark:text-violet-100',
  'bg-fuchsia-100 text-fuchsia-700 dark:bg-fuchsia-500/25 dark:text-fuchsia-100',
] as const;

/** Neutral slate for a contact with no seed at all — never a random colour. */
const AVATAR_NEUTRAL = 'bg-gray-100 text-gray-500 dark:bg-gray-700 dark:text-gray-300';

/** FNV-1a. Any stable 32-bit hash would do; this one is short and dependency-free. */
function hashSeed(seed: string): number {
  let hash = 2166136261;
  for (let i = 0; i < seed.length; i += 1) {
    hash ^= seed.charCodeAt(i);
    hash = Math.imul(hash, 16777619);
  }
  return hash >>> 0;
}

/**
 * The placeholder's background/foreground classes for a given seed — normally
 * the contact id. Exported so the pages and the tests agree on one function
 * rather than each re-deriving a colour.
 */
export function avatarPaletteClasses(seed?: string | null): string {
  if (!seed) return AVATAR_NEUTRAL;
  return AVATAR_PALETTE[hashSeed(seed) % AVATAR_PALETTE.length];
}

/**
 * First + last name part. `Customer.full_name` is first + middle + last
 * (`apps/customers/models.py::save`), so the middle name is skipped: "Alice
 * Renu Sharma" is AS, the initials a human would write.
 */
export function contactInitials(name?: string | null): string {
  const parts = (name ?? '').trim().split(/\s+/).filter(Boolean);
  if (parts.length === 0) return '';
  const first = parts[0][0] ?? '';
  const last = parts.length > 1 ? parts[parts.length - 1][0] ?? '' : '';
  return `${first}${last}`.toUpperCase();
}

export type ContactAvatarSize = 'xs' | 'sm' | 'md' | 'lg' | 'xl';

/**
 * Box size plus the `sizes` hint that goes with it. The hint is derived here,
 * not taken from the brief's own `sizes`: the serializer's value is tuned for
 * the page-wide artwork (insurer banners) that field was built for and would
 * make the browser fetch a 1600px rendition for a 40px circle.
 */
const SIZE_STYLES: Record<ContactAvatarSize, { box: string; text: string; glyph: string; sizes: string }> = {
  xs: { box: 'w-8 h-8', text: 'text-xs', glyph: 'w-4 h-4', sizes: '32px' },
  sm: { box: 'w-10 h-10', text: 'text-sm', glyph: 'w-5 h-5', sizes: '40px' },
  md: { box: 'w-12 h-12', text: 'text-base', glyph: 'w-6 h-6', sizes: '48px' },
  lg: { box: 'w-16 h-16', text: 'text-xl', glyph: 'w-8 h-8', sizes: '64px' },
  xl: { box: 'w-24 h-24', text: 'text-3xl', glyph: 'w-12 h-12', sizes: '96px' },
};

interface ContactAvatarProps {
  /** Seeds the placeholder colour. The contact's id — stable across renames. */
  contactId?: string | null;
  /** Usually `customer.full_name`; supplies the initials and the alt text. */
  name?: string | null;
  /** The API's `avatar_image`. `null`/`undefined` simply means "no picture". */
  image?: ContactAvatarImage | null;
  size?: ContactAvatarSize;
  className?: string;
}

export function ContactAvatar({
  contactId,
  name,
  image,
  size = 'sm',
  className,
}: ContactAvatarProps) {
  // Remembering the src that failed (rather than a bare boolean) means a new
  // picture — the moment an upload replaces a dead one — re-arms the <img>
  // without any reset effect.
  const [failedSrc, setFailedSrc] = useState<string | null>(null);

  const styles = SIZE_STYLES[size];
  const src = image?.src || image?.url || image?.stable_url || '';
  const showImage = Boolean(src) && failedSrc !== src;
  const srcSet = image?.srcset_webp || image?.srcset || '';
  const initials = contactInitials(name);

  if (showImage) {
    return (
      <div
        className={cn(
          'relative shrink-0 overflow-hidden rounded-full bg-gray-100 dark:bg-gray-700',
          styles.box,
          className
        )}
        // The asset's average colour behind the picture, so there is no white
        // flash before it decodes.
        style={image?.placeholder_color ? { backgroundColor: image.placeholder_color } : undefined}
      >
        <img
          src={src}
          srcSet={srcSet || undefined}
          sizes={srcSet ? styles.sizes : undefined}
          alt={name || image?.alt_text || ''}
          loading="lazy"
          decoding="async"
          onError={() => setFailedSrc(src)}
          className="h-full w-full object-cover"
        />
      </div>
    );
  }

  return (
    <div
      className={cn(
        'shrink-0 rounded-full flex items-center justify-center font-medium',
        styles.box,
        styles.text,
        avatarPaletteClasses(contactId || name),
        className
      )}
      // The initials are decorative — the contact's name is always visible text
      // beside this circle on every screen that mounts it — so the box gets a
      // title for hover only and no role/aria-label that would double-announce.
      title={name || undefined}
    >
      {initials ? (
        <span aria-hidden="true">{initials}</span>
      ) : (
        <UserIcon className={styles.glyph} aria-hidden="true" />
      )}
    </div>
  );
}

/**
 * Both avatar endpoints (`POST /customers/{id}/avatar/` and
 * `POST /customers/me/avatar/`) answer a rejected upload with a bare
 * `{"file": "<reason>"}` — a plain `Response`, so the backend's plain-language
 * exception handler never reshapes it and `getErrorMessage` cannot see it. Read
 * that key first, then fall back to the shared reader for everything else
 * (auth, throttling, 413 from nginx).
 */
export function avatarUploadErrorMessage(error: unknown): string {
  const data = (error as { response?: { data?: unknown } } | undefined)?.response?.data;
  if (data && typeof data === 'object') {
    const fileError = (data as Record<string, unknown>).file;
    if (typeof fileError === 'string' && fileError) return fileError;
  }
  return getErrorMessage(error);
}

interface ContactAvatarPickerProps {
  contactId?: string | null;
  name?: string | null;
  image?: ContactAvatarImage | null;
  onSelect: (file: File) => void;
  onRemove?: () => void;
  isBusy?: boolean;
  error?: string | null;
  disabled?: boolean;
  /** Copy under the buttons; defaults to the server's own accepted-input rules. */
  hint?: string;
}

/**
 * The avatar plus its upload/replace/remove controls. Purely presentational —
 * it never talks to the API, because the two callers post to two different
 * endpoints (broker staff to `/customers/{id}/avatar/`, the contact themselves
 * to `/customers/me/avatar/`) and the choice of endpoint is a permission
 * decision that belongs on the page, not in a shared widget.
 */
export function ContactAvatarPicker({
  contactId,
  name,
  image,
  onSelect,
  onRemove,
  isBusy = false,
  error,
  disabled = false,
  hint = 'JPG, PNG or WebP, up to 15 MB. The picture is resized and optimised on upload.',
}: ContactAvatarPickerProps) {
  const inputId = useId();
  const hasPicture = Boolean(image?.src || image?.url || image?.stable_url);

  return (
    <div className="flex items-center gap-5">
      <ContactAvatar contactId={contactId} name={name} image={image} size="xl" />
      <div className="min-w-0">
        <div className="flex flex-wrap items-center gap-3">
          <label
            htmlFor={inputId}
            className={cn(
              'inline-flex items-center rounded-lg border border-gray-300 px-3 py-2 text-sm font-medium text-gray-700 dark:border-gray-600 dark:text-gray-200',
              disabled || isBusy
                ? 'cursor-not-allowed opacity-50'
                : 'cursor-pointer hover:bg-gray-50 dark:hover:bg-gray-700'
            )}
          >
            {isBusy ? 'Uploading…' : hasPicture ? 'Replace photo' : 'Upload photo'}
          </label>
          <input
            id={inputId}
            type="file"
            accept="image/*"
            disabled={disabled || isBusy}
            className="sr-only"
            onChange={(event) => {
              const file = event.target.files?.[0];
              // Reset the input's value so re-picking the SAME file after a
              // failed upload still fires a change event.
              event.target.value = '';
              if (file) onSelect(file);
            }}
          />
          {hasPicture && onRemove && (
            <button
              type="button"
              disabled={disabled || isBusy}
              onClick={onRemove}
              className="text-sm font-medium text-red-600 hover:text-red-700 dark:hover:text-red-300 disabled:opacity-50 dark:text-red-400"
            >
              Remove
            </button>
          )}
        </div>
        <p className="mt-2 text-xs text-gray-500 dark:text-gray-400">{hint}</p>
        {error && <p className="mt-1 text-xs text-red-600 dark:text-red-400">{error}</p>}
      </div>
    </div>
  );
}
